import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ExternalNetworkScanListQueryDto } from '@terra/shared-contracts-external-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import { isScanStillRunning } from '../../utils/external-network-scan.utils';

export const EXTERNAL_NETWORK_SCANS_KEY = 'external-network-scans';

const STALE_TIME_MS = 10_000;
const ACTIVE_SCAN_REFETCH_INTERVAL_MS = 10_000;

export function useExternalNetworkScans({
  surfaceId,
  query = {},
  refetchInterval,
}: {
  surfaceId: string | null;
  query?: ExternalNetworkScanListQueryDto;
  refetchInterval?: number | false;
}) {
  const result = useQuery({
    queryKey: [EXTERNAL_NETWORK_SCANS_KEY, surfaceId, query],
    queryFn: () =>
      terraProxyApiClient.listExternalNetworkScans({
        surfaceId: surfaceId ?? '',
        query,
      }),
    enabled: Boolean(surfaceId),
    staleTime: STALE_TIME_MS,
    refetchInterval,
  });

  return {
    scans: result.data?.data ?? [],
    total: result.data?.total ?? 0,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    refetch: result.refetch,
  };
}

export function useActiveExternalNetworkScan({
  surfaceId,
}: {
  surfaceId: string | null;
}) {
  const { scans, isLoading } = useExternalNetworkScans({
    surfaceId,
    query: { limit: 5, skip: 0 },
    refetchInterval: ACTIVE_SCAN_REFETCH_INTERVAL_MS,
  });

  const activeScan = useMemo(() => {
    const activeScans = scans.filter((scan) => isScanStillRunning(scan));
    if (activeScans.length === 0) {
      return undefined;
    }
    return activeScans.reduce((earliest, scan) =>
      new Date(scan.createdAt).getTime() <
      new Date(earliest.createdAt).getTime()
        ? scan
        : earliest,
    );
  }, [scans]);

  return {
    activeScan,
    isLoading,
  };
}
